import React, { useState, useEffect, useRef, useCallback, use } from 'react';
import { Search, Folder, File, Download, Upload, Grid, List, Filter, ChevronRight, Home, FileText, Image, Archive, Eye, Edit, Share, Move, Trash2, MoreVertical, Tag, Star, Clock, User, Calendar, ArrowDown, FolderOpen, FolderClosed, Settings, LucideBookmarkPlus, Album, SquareLibrary, GalleryVerticalEnd, GalleryVertical, Loader2, Ellipsis, ArrowDownToLine, X, ChevronDown, Copy, ArrowDownAZ, BookCopy } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from './Modal';
import FileEditor from './FileEditor';
import '../styles/LibraryDocuments.css';
import '../styles/Documents.css';
import { SearchBar } from './Nav';
import { Checkbox, formatValue, SearchSelect } from './FieldComponents';
import { fetchUsers } from './CreateLead';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { useUploads } from './UploadManagerContext';
import { useNavVariant } from '../App';
import { beginDownloadNotification, markDownloadNotificationStarted, dismissDownloadNotification } from '../utils/downloadNotifications';
import simpleUploadService from '../services/simpleUploadService';
import { bindLibraryCaseItems, resolveLibraryMutationScope, libraryMutationDefaultDestination, resolveLibraryMutationDestination } from '../utils/libraryMutationScope';

const Library = ({ user_id }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dispatch } = useUploads(); // Get dispatch from upload context
    const navVariant = useNavVariant();
    
    // Core state
    const [view, setView] = useState(0); // 0: library, 1: cases, 2: firm-wide
    const [folderPath, setFolderPath] = useState('');
    const [files, setFiles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [cases, setCases] = useState([]); // For view 1: cases list
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        pageSize: 100,
        totalCount: 0
    });

    const [currentAction, setCurrentAction] = useState(null);

    // Search and filtering
    const [searchQuery, setSearchQuery] = useState('');
    const [useSemanticSearch, setUseSemanticSearch] = useState(false);
    const [isSearchPending, setIsSearchPending] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null); // When drilling into a case
    const [extensionFilter, setExtensionFilter] = useState('');
    const [firstPrimaryFilter, setFirstPrimaryFilter] = useState('');
    const [firstPrimaryUsers, setFirstPrimaryUsers] = useState([]);
    const [docGenFolderLabels, setDocGenFolderLabels] = useState({});
    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [caseInfo, setCaseInfo] = useState(null); // Current case info when drilling in

    // Selection and actions
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedFolders, setSelectedFolders] = useState([]);
    const [showBulkActions, setShowBulkActions] = useState(false);

    // Modals and editors
    const [showFileEditor, setShowFileEditor] = useState(false);
    const [fileToEdit, setFileToEdit] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveTargetFolder, setMoveTargetFolder] = useState('');
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [newFileName, setNewFileName] = useState('');
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyTarget, setCopyTarget] = useState(null);
    const [copyTargetFolder, setCopyTargetFolder] = useState('');

    // Preview
    const [sidePreview, setSidePreview] = useState(null);
    const [sidePreviewFile, setSidePreviewFile] = useState(null);
    const [sidePreviewObjectUrl, setSidePreviewObjectUrl] = useState(null);
    const [filePreview, setFilePreview] = useState(null); // Full file display modal

    // Utilities
    const fileInputRef = useRef(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [actionTarget, setActionTarget] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState('bottom'); // 'bottom' or 'top'
    const dropdownRef = useRef(null);
    const searchAbortControllerRef = useRef(null);
    const firmWideAbortControllerRef = useRef(null);
    const firmWideRequestSequenceRef = useRef(0);
    const hasInitializedSearchEffectRef = useRef(false);
    const hasInitializedFilterEffectRef = useRef(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);

    // Calculate dropdown position based on available space
    const handleDropdownClick = (e, itemKey) => {
        e.stopPropagation();
        
        if (activeDropdown === itemKey) {
            setActiveDropdown(null);
            return;
        }
        
        const buttonRect = e.currentTarget.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const dropdownHeight = 250; // Approximate dropdown height
        
        // If not enough space below and more space above, show dropdown above
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition('top');
        } else {
            setDropdownPosition('bottom');
        }
        
        setActiveDropdown(itemKey);
    };

    useEffect(() => {
        setSearchQuery('');
    }, [view]);

    useEffect(() => {
        document.title = "Library | CaseDB";
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadDocGenFolderLabels = async () => {
            try {
                const response = await fetch('https://api.casedb.co/library.php?mode=docgen-folder-labels', {
                    credentials: 'include'
                });
                const data = await response.json();
                if (!cancelled && data.success) {
                    setDocGenFolderLabels(data.labels || {});
                }
            } catch (error) {
                console.error('Error loading DocGen folder labels:', error);
            }
        };

        loadDocGenFolderLabels();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        return () => {
            if (sidePreviewObjectUrl) {
                URL.revokeObjectURL(sidePreviewObjectUrl);
            }
        };
    }, [sidePreviewObjectUrl]);
    
    // URL parameter management
    const updateURL = (params) => {
        const searchParams = new URLSearchParams(location.search);
        
        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                searchParams.delete(key);
            } else {
                searchParams.set(key, value);
            }
        });
        
        const newSearch = searchParams.toString();
        const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
        
        // Update URL without triggering navigation
        window.history.replaceState({}, '', newPath);
    };

    const parseURLParams = () => {
        const searchParams = new URLSearchParams(location.search);
        const viewParam = searchParams.get('view');
        let parsedView = null;
        if (viewParam !== null && viewParam !== '') {
            const viewValue = parseInt(viewParam, 10);
            if (!Number.isNaN(viewValue) && viewValue >= 0 && viewValue <= 2) {
                parsedView = viewValue;
            }
        }
        return {
            case: searchParams.get('case') || null,
            folder: searchParams.get('folder') || '',
            search: searchParams.get('search') || '',
            view: parsedView
        };
    };

    // Load library files
    const loadLibraryFiles = async (path = '', page = 1, signal = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                folder: path || 'library',
                page: page.toString(),
                page_size: pagination.pageSize.toString(),
                mode: 'library'
            });

            if (extensionFilter) {
                params.append('extension', extensionFilter);
            }

            const response = await fetch(`https://api.casedb.co/library.php?${params}`, {
                credentials: 'include',
                signal: signal
            });
            
            const data = await response.json();
            
            if (data.success) {
                setFiles(data.files || []);
                setFolders(data.folders || []);
                setCases([]); // Clear cases when showing library
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.totalPages || 1,
                    totalCount: data.pagination?.totalCount || 0
                }));
            } else {
                console.error('Failed to load library files:', data.message);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Library files fetch cancelled');
            } else {
                console.error('Error loading library files:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const isDocGenPath = useCallback((path = folderPath) => {
        const normalized = String(path || '').trim().replace(/^\/+|\/+$/g, '').toLowerCase();
        return normalized === 'docgen' || normalized.startsWith('docgen/') || normalized === 'library/docgen' || normalized.startsWith('library/docgen/');
    }, [folderPath]);

    const getDocGenPathParts = useCallback((path = folderPath) => {
        const normalized = String(path || '')
            .trim()
            .replace(/^\/+|\/+$/g, '')
            .replace(/^library\/docgen\/?/i, '')
            .replace(/^docgen\/?/i, '');

        return normalized ? normalized.split('/').filter(Boolean) : [];
    }, [folderPath]);

    const shouldReplaceDocGenFolderName = useCallback((folderName, parentPath = folderPath) => {
        return isDocGenPath(parentPath)
            && getDocGenPathParts(parentPath).length === 1
            && /^\d+$/.test(String(folderName || ''));
    }, [folderPath, getDocGenPathParts, isDocGenPath]);

    const isOfficeFile = useCallback((file) => {
        const fileName = String(file?.name || file?.file_name || '');
        const ext = fileName.split('.').pop()?.toLowerCase();
        return ['doc', 'docx', 'docm', 'xls', 'xlsx', 'xlsm', 'ppt', 'pptx', 'pptm'].includes(ext);
    }, []);

    const getDocGenFolderLabel = useCallback((folderName, parentPath = folderPath) => {
        if (!shouldReplaceDocGenFolderName(folderName, parentPath)) {
            return folderName;
        }

        return docGenFolderLabels[String(folderName)] || folderName;
    }, [docGenFolderLabels, folderPath, shouldReplaceDocGenFolderName]);

    const getFolderDisplayName = useCallback((folder) => {
        const folderName = typeof folder === 'string' ? folder : (folder?.name || '');
        if (typeof folder === 'object' && folder?.display_name) return folder.display_name;
        if (!folderName) return '';
        if (folderName.toLowerCase() === 'docgen') return 'DocGen';
        if ((typeof folder === 'object' && folder?.is_docgen_virtual) || isDocGenPath()) {
            return getDocGenFolderLabel(folderName);
        }
        return folderName;
    }, [getDocGenFolderLabel, isDocGenPath]);

    const getFolderNavigationName = useCallback((folder) => {
        if (typeof folder === 'string') return folder;
        return folder?.navigation_name || folder?.name || '';
    }, []);

    // Load cases for view 1
    const loadCases = async (page = 1, signal = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                mode: 'cases',
                page: page.toString(),
                page_size: pagination.pageSize.toString()
            });

            const response = await fetch(`https://api.casedb.co/library.php?${params}`, {
                credentials: 'include',
                signal: signal
            });

            const data = await response.json();
            if (data.success) {
                setCases(data.cases || []);
                setFiles([]); // Clear files when showing cases
                setFolders([]); // Clear folders when showing cases
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.totalPages || 1,
                    totalCount: data.pagination?.totalCount || 0
                }));
            } else {
                console.error('Failed to load cases:', data.message);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Cases fetch cancelled');
            } else {
                console.error('Error loading cases:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Load case documents when drilling into a case
    const loadCaseDocuments = async (caseId, path = '', page = 1, signal = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                mode: 'cases',
                case_id: caseId,
                folder: path || '{{Name}}',
                page: page.toString(),
                page_size: pagination.pageSize.toString()
            });

            if (extensionFilter) {
                params.append('extension', extensionFilter);
            }

            const response = await fetch(`https://api.casedb.co/library.php?${params}`, {
                credentials: 'include',
                signal: signal
            });

            const data = await response.json();
            if (data.success) {
                const caseFiles = bindLibraryCaseItems(data.files || [], data.case_info?.id, caseId);
                const caseFolders = bindLibraryCaseItems(data.folders || [], data.case_info?.id, caseId);
                setFiles(caseFiles);
                setFolders(caseFolders);
                setCases([]); // Clear cases when showing case documents
                setCaseInfo(data.case_info);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.totalPages || 1,
                    totalCount: data.pagination?.totalCount || 0
                }));
            } else {
                console.error('Failed to load case documents:', data.message);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Case documents fetch cancelled');
            } else {
                console.error('Error loading case documents:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Global search across cases and files
    const performGlobalSearch = async (page = 1, signal = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                search: searchQuery,
                page: page.toString(),
                limit: pagination.pageSize.toString()
            });

            const response = await fetch(`https://api.casedb.co/search-cases-wide.php?${params}`, {
                credentials: 'include',
                signal: signal
            });

            const data = await response.json();
            if (data.success) {
                // Process results to separate cases and files
                const searchCases = data.results.filter(r => r.result_type === 'case');
                const searchFiles = data.results.filter(r => r.result_type === 'file');
                
                setCases(searchCases);
                setFiles(searchFiles);
                setFolders([]); // No folders in search results
                
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.total_pages || 1,
                    totalCount: data.pagination?.total_results || 0
                }));
            } else {
                console.error('Failed to perform global search:', data.message);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Global search fetch cancelled');
            } else {
                console.error('Error performing global search:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Load firm-wide files (view 2): all library files + all case files flattened
    const loadFirmWideFiles = async (page = 1, signal = null) => {
        const requestId = ++firmWideRequestSequenceRef.current;
        if (firmWideAbortControllerRef.current) {
            firmWideAbortControllerRef.current.abort();
        }

        const requestController = new AbortController();
        firmWideAbortControllerRef.current = requestController;
        const handleExternalAbort = () => requestController.abort();

        if (signal) {
            if (signal.aborted) {
                requestController.abort();
            } else {
                signal.addEventListener('abort', handleExternalAbort, { once: true });
            }
        }

        setLoading(true);
        try {
            const hasSearchQuery = searchQuery && searchQuery.trim().length >= 2;
            const params = new URLSearchParams({
                mode: 'firm-wide',
                page: page.toString(),
                page_size: pagination.pageSize.toString()
            });

            if (hasSearchQuery) {
                params.append('search', searchQuery.trim());
                if (useSemanticSearch) {
                    params.append('semantic', 'true');
                }
            }

            if (extensionFilter) {
                params.append('extension', extensionFilter);
            }

            if (firstPrimaryFilter) {
                params.append('first_primary_id', firstPrimaryFilter.toString());
            }

            const response = await fetch(`https://api.casedb.co/library.php?${params}`, {
                credentials: 'include',
                signal: requestController.signal
            });

            const data = await response.json();
            if (requestId !== firmWideRequestSequenceRef.current || requestController.signal.aborted) {
                return null;
            }

            if (data.success) {
                setFiles(data.files || []);
                setFolders([]); // No folders in firm-wide view
                setCases([]); // No cases in firm-wide view
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.totalPages || 1,
                    totalCount: data.pagination?.totalCount || 0
                }));
            } else {
                console.error('Failed to load firm-wide files:', data.message);
            }
        } catch (error) {
            if (requestId !== firmWideRequestSequenceRef.current) {
                return;
            }
            if (error.name === 'AbortError') {
                console.log('Firm-wide files fetch cancelled');
            } else {
                console.error('Error loading firm-wide files:', error);
            }
        } finally {
            if (signal) {
                signal.removeEventListener('abort', handleExternalAbort);
            }
            if (firmWideAbortControllerRef.current === requestController) {
                firmWideAbortControllerRef.current = null;
            }
            if (requestId === firmWideRequestSequenceRef.current) {
                setLoading(false);
            }
        }
    };

    // File redirect by ID (for moved files)
    const redirectToFile = async (fileId) => {
        try {
            const response = await fetch(`https://api.casedb.co/file-redirect.php?file_id=${fileId}`, {
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Navigate to the file's current location
                window.location.href = data.redirect_url;
            } else {
                console.error('File not found or moved:', data.message);
                alert('File not found. It may have been moved or deleted.');
            }
        } catch (error) {
            console.error('Error redirecting to file:', error);
            alert('Error finding file location.');
        }
    };

    // Handle view changes
    const handleViewChange = (newView) => {
        // Set loading immediately to prevent showing stale data
        setLoading(true);
        
        setView(newView);
        if (newView !== 2) {
            setUseSemanticSearch(false);
        }
        if (newView !== 2) {
            setFirstPrimaryFilter('');
        }
        setFolderPath('');
        setSelectedCase(null);
        setCaseInfo(null);
        setSelectedFiles([]);
        setSelectedFolders([]);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        
        // Clear old data immediately to prevent remnants
        setFiles([]);
        setFolders([]);
        setCases([]);

        // Update URL
        updateURL({
            view: newView,
            case: null,
            folder: null
        });

        // Don't call load functions here - let the search useEffect handle it
        // The useEffect will trigger because we cleared the arrays and may have searchQuery
    };

    // Handle case selection (when clicking on a case in view 1)
    const handleCaseClick = (caseItem) => {
        setSelectedCase(caseItem);
        setFolderPath('');
        
        // Update URL
        updateURL({
            case: caseItem.id,
            folder: null
        });
        
        loadCaseDocuments(caseItem.id);
    };

    // Handle folder navigation
    const handleFolderClick = (folder) => {
        const folderName = getFolderNavigationName(folder);
        const newPath = folderPath ? `${folderPath}/${folderName}` : folderName;
        setFolderPath(newPath);
        
        // Update URL
        if (view === 0) {
            updateURL({ folder: newPath });
        } else if (selectedCase) {
            updateURL({ 
                case: selectedCase.id,
                folder: newPath 
            });
        }
        
        if (view === 0) {
            // Library mode
            loadLibraryFiles(newPath);
        } else if (selectedCase) {
            // Case documents mode
            loadCaseDocuments(selectedCase.id, newPath);
        }
    };

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = (index) => {
        const pathParts = folderPath.split('/');
        const newPath = pathParts.slice(0, index + 1).join('/');
        setFolderPath(newPath);
        
        if (view === 0) {
            // Library mode
            loadLibraryFiles(newPath);
        } else if (selectedCase) {
            // Case documents mode
            loadCaseDocuments(selectedCase.id, newPath);
        }
    };

    // Go back to cases list from case documents
    const handleBackToCases = () => {
        setSelectedCase(null);
        setCaseInfo(null);
        setFolderPath('');
        
        // Update URL
        updateURL({
            case: null,
            folder: null
        });
        
        loadCases();
    };

    // File operations (similar to DocumentSection)
    const handleFilePreview = async (file) => {
        setSidePreviewFile(file);
        
        if (file.type === 'folder' || file.isFolder) {
            const folderDisplayName = getFolderDisplayName(file);
            setSidePreview(
                <div className="folder-preview">
                    <div className="preview-header">
                        <Folder size={24} />
                        <h3>{folderDisplayName}</h3>
                    </div>
                    <div className="folder-info">
                        <p><strong>Type:</strong> Folder</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatDate(file.last_modified)}</p>}
                        <p><strong>Path:</strong> {folderPath}/{folderDisplayName}</p>
                    </div>
                    <div className="folder-actions">
                        <button 
                            className="action" 
                            onClick={() => handleFolderClick(file)}
                        >
                            <Folder size={16} /> Open Folder
                        </button>
                    </div>
                </div>
            );
            return;
        }

        const fileType = file.name.split('.').pop().toLowerCase();
        
        setSidePreview(
            <div className="loading-preview">
                <Loader2 className="animate-spin" />
                <span>Loading preview...</span>
            </div>
        );

        try {
            let preview;
            const fileIdentifier = file.storage_key || file.key;

            setSidePreviewObjectUrl(null);
            
            if (fileType === "pdf") {
                const response = await fetch(`https://api.casedb.co/pdf-proxy.php?fileKey=${encodeURIComponent(fileIdentifier)}&cases=true`);
                
                if (!response.ok) {
                    throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
                }
                
                const contentType = response.headers.get('Content-Type');
                
                if (contentType && contentType.includes('application/pdf')) {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    setSidePreviewObjectUrl(url);
                    
                    preview = (
                        <div className="pdf-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <iframe
                                src={url}
                                title={file.name}
                                width="100%"
                                height="400px"
                                className="pdf-preview"
                            />
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => handleOfficeEdit(file)}
                                >
                                    <Edit size={16} /> Edit in Office
                                </button>
                                <button 
                                    className="action" 
                                    onClick={() => handleFileDoubleClick(file)}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(file)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                } else {
                    const result = await response.json();
                    if (result.error) {
                        throw new Error(result.error);
                    }
                    
                    if (result.type === 'base64') {
                        const pdfData = `data:application/pdf;base64,${result.data}`;
                        preview = (
                            <div className="pdf-preview-container">
                                <div className="preview-header">
                                    <h3>{result.filename}</h3>
                                </div>
                                <object
                                    data={pdfData}
                                    type="application/pdf"
                                    width="100%"
                                    height="400px"
                                    className="pdf-preview"
                                />
                                <div className="preview-actions">
                                    <button 
                                        className="action" 
                                        onClick={() => handleFileDoubleClick(file)}
                                    >
                                        <Eye size={16} /> Full View
                                    </button>
                                    <button 
                                        className="action alt" 
                                        onClick={() => handleFileDownload(file)}
                                    >
                                        <Download size={16} /> Download
                                    </button>
                                </div>
                            </div>
                        );
                    }
                }
            } else if (fileType === 'eml') {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                
                if (result.success) {
                    const textResponse = await fetch(result.url);
                    const emlText = await textResponse.text();
                    const truncatedText = emlText.length > 2000 ? emlText.substring(0, 2000) + '...' : emlText;

                    preview = (
                        <div className="email-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <div className="email-preview" style={{ 
                                padding: "15px", 
                                whiteSpace: "pre-wrap", 
                                fontSize: "12px",
                                lineHeight: "1.4",
                                backgroundColor: "var(--secondary-color)",
                                border: "1px solid var(--border-color)",
                                borderRadius: '6px',
                                maxHeight: "300px",
                                overflowY: "auto"
                            }}>
                                {truncatedText}
                            </div>
                            {emlText.length > 2000 && (
                                <p className="truncation-notice">Email content truncated. Full content available on full view.</p>
                            )}
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => handleFileDoubleClick(file)}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(file)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                }
            } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                
                if (result.success) {
                    const officePreviewUrl = result.office_preview_url || result.url;
                    preview = (
                        <div className="office-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <iframe
                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(officePreviewUrl)}&cases=true`}
                                width="100%"
                                height="400px"
                                frameBorder="0"
                                className="office-preview"
                            />
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => handleFileDoubleClick(file)}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(file)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                }
            } else if (['txt', 'csv', 'log'].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                
                if (result.success) {
                    const textResponse = await fetch(result.url);
                    const textContent = await textResponse.text();
                    const truncatedText = textContent.length > 2000 ? textContent.substring(0, 2000) + '...' : textContent;

                    preview = (
                        <div className="text-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <div className="text-preview" style={{ 
                                padding: "15px", 
                                whiteSpace: "pre-wrap", 
                                fontSize: "12px",
                                lineHeight: "1.4",
                                backgroundColor: "var(--secondary-color)",
                                border: "1px solid var(--border-color)",
                                borderRadius: '6px',
                                maxHeight: "300px",
                                overflowY: "auto",
                                fontFamily: "monospace"
                            }}>
                                {truncatedText}
                            </div>
                            {textContent.length > 2000 && (
                                <p className="truncation-notice">Content truncated. Full content available on full view.</p>
                            )}
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => handleFileDoubleClick(file)}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(file)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                }
            } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                
                if (result.success) {
                    preview = (
                        <div className="image-preview-container">
                            <div className="preview-header">
                                <h3>{file.name}</h3>
                            </div>
                            <img
                                src={result.url}
                                alt={file.name}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "400px",
                                    objectFit: "contain"
                                }}
                                className="image-preview"
                            />
                            <div className="preview-actions">
                                <button 
                                    className="action" 
                                    onClick={() => handleFileDoubleClick(file)}
                                >
                                    <Eye size={16} /> Full View
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileDownload(file)}
                                >
                                    <Download size={16} /> Download
                                </button>
                            </div>
                        </div>
                    );
                }
            } else {
                // Default preview for unsupported file types
                preview = (
                    <div className="file-info-preview">
                        <div className="preview-header">
                            <File size={24} />
                            <h3>{file.name}</h3>
                        </div>
                        <div className="file-details">
                            <p><strong>Type:</strong> {fileType.toUpperCase()}</p>
                            <p><strong>Size:</strong> {formatFileSize(file.size)}</p>
                            {file.lastModified && <p><strong>Modified:</strong> {formatDate(file.lastModified)}</p>}
                        </div>
                        <div className="preview-actions">
                            <button 
                                className="action" 
                                onClick={() => handleFileDownload(file)}
                            >
                                <Download size={16} /> Download
                            </button>
                            {['txt', 'js', 'css', 'html', 'json', 'xml'].includes(fileType) && (
                                <button 
                                    className="action alt" 
                                    onClick={() => handleFileEdit(file)}
                                >
                                    <Edit size={16} /> Edit
                                </button>
                            )}
                        </div>
                    </div>
                );
            }

            setSidePreview(preview);

        } catch (error) {
            console.error('Error loading file preview:', error);
            setSidePreview(
                <div className="error-preview">
                    <div className="preview-header">
                        <h3>{file.name}</h3>
                    </div>
                    <p>Error loading preview: {error.message}</p>
                    <div className="preview-actions">
                        <button 
                            className="action" 
                            onClick={() => handleFileDownload(file)}
                        >
                            <Download size={16} /> Download
                        </button>
                    </div>
                </div>
            );
        }
    };

    const handleFileEdit = (file) => {
        setFileToEdit(file);
        setShowFileEditor(true);
    };

    const handleFileDownload = async (file) => {
        const notificationId = beginDownloadNotification(file?.name);
        try {
            const fileIdentifier = file.storage_key || file.key;
            const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.success) {
                // Fetch the file as a blob
                const fileResponse = await fetch(data.url);
                const blob = await fileResponse.blob();
                
                // Create a download link from the blob
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the blob URL
                window.URL.revokeObjectURL(blobUrl);
                markDownloadNotificationStarted(notificationId);
            } else {
                dismissDownloadNotification(notificationId);
            }
        } catch (error) {
            dismissDownloadNotification(notificationId);
            console.error('Error downloading file:', error);
        }
    };

    const refreshAfterLibraryMutation = () => {
        if (view === 0) {
            loadLibraryFiles(folderPath, pagination.currentPage);
        } else if (view === 2) {
            loadFirmWideFiles(pagination.currentPage);
        } else if (selectedCase) {
            loadCaseDocuments(selectedCase.id, folderPath, pagination.currentPage);
        } else if (searchQuery.trim().length >= 2) {
            performGlobalSearch(pagination.currentPage);
        }
    };

    // Delete file handler
    const handleFileDelete = async (file) => {
        if (!window.confirm(`Are you sure you want to delete "${file.name}"? This will move it to the deleted folder.`)) {
            return;
        }

        try {
            const scope = resolveLibraryMutationScope([file]);
            let data;
            if (scope.kind === 'case') {
                data = await simpleUploadService.deleteFiles(scope.keys, scope.caseId);
            } else {
                const response = await fetch('https://api.casedb.co/library.php', {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        keys: scope.keys
                    })
                });
                data = await response.json();
            }

            if (data.success) {
                refreshAfterLibraryMutation();
            } else {
                alert('Failed to delete file: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            alert(error.message || 'Failed to delete file. Please try again.');
        }
    };

    // Rename file handler
    const handleRenameFile = (file) => {
        setRenameTarget(file);
        // Extract filename without extension for easier editing
        const nameParts = file.name.split('.');
        const extension = nameParts.length > 1 ? nameParts.pop() : '';
        const baseName = nameParts.join('.');
        setNewFileName(baseName);
        setShowRenameModal(true);
    };

    const handleRenameSubmit = async () => {
        if (renameSubmitting) return;
        if (!newFileName.trim() || !renameTarget) {
            alert('Please enter a valid name');
            return;
        }

        setRenameSubmitting(true);
        try {
            let finalName;
            
            if (renameTarget.isFolder) {
                // For folders, just use the new name without extension
                finalName = newFileName.trim();
            } else {
                // For files, get extension from original name
                const originalExt = renameTarget.name.split('.').pop();
                finalName = newFileName.trim() + (originalExt ? `.${originalExt}` : '');
            }

            const scope = resolveLibraryMutationScope([renameTarget]);
            if (scope.kind === 'case') {
                const result = await simpleUploadService.renameFile(
                    scope.keys[0],
                    finalName,
                    scope.caseId,
                    renameTarget.isFolder || false,
                    { dispatch },
                    {
                        onAccepted: () => {
                            // Keep the background status visible while the worker
                            // finishes; refresh the case only at terminal success.
                            setShowRenameModal(false);
                            setRenameTarget(null);
                            setNewFileName('');
                            setRenameSubmitting(false);
                        }
                    }
                );

                if (!result.success) {
                    alert('Failed to rename: ' + (result.message || result.error || 'Unknown error'));
                    return;
                }

                setShowRenameModal(false);
                setRenameTarget(null);
                setNewFileName('');
                refreshAfterLibraryMutation();
                return;
            }

            const response = await fetch('https://api.casedb.co/library.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'rename',
                    old_key: scope.keys[0],
                    new_name: finalName,
                    is_folder: renameTarget.isFolder || false
                })
            });

            const data = await response.json();

            if (data.success) {
                setShowRenameModal(false);
                setRenameTarget(null);
                setNewFileName('');
                
                refreshAfterLibraryMutation();
            } else {
                alert('Failed to rename: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error renaming:', error);
            alert(error.message || 'Failed to rename. Please try again.');
        } finally {
            setRenameSubmitting(false);
        }
    };

    // Move file handler
    const handleMoveFile = (file) => {
        try {
            const scope = resolveLibraryMutationScope([file]);
            setActionTarget(file);
            setMoveTargetFolder(libraryMutationDefaultDestination(scope));
            setShowMoveModal(true);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleMoveSubmit = async () => {
        if (!actionTarget) return;

        try {
            const scope = resolveLibraryMutationScope([actionTarget]);
            const targetFolder = resolveLibraryMutationDestination(scope, moveTargetFolder);
            let data;
            if (scope.kind === 'case') {
                data = await simpleUploadService.moveFiles(scope.keys, targetFolder, scope.caseId, { dispatch });
            } else {
                const response = await fetch('https://api.casedb.co/library.php', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        action: 'move',
                        keys: scope.keys,
                        target_folder: targetFolder
                    })
                });
                data = await response.json();
            }

            if (data.success) {
                setShowMoveModal(false);
                setActionTarget(null);
                setMoveTargetFolder('');
                refreshAfterLibraryMutation();
            } else {
                alert('Failed to move file: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error moving file:', error);
            alert(error.message || 'Failed to move file. Please try again.');
        }
    };

    // Copy file handler
    const handleCopyFile = (file) => {
        try {
            const scope = resolveLibraryMutationScope([file]);
            setCopyTarget(file);
            setCopyTargetFolder(libraryMutationDefaultDestination(scope));
            setShowCopyModal(true);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleCopySubmit = async () => {
        if (!copyTarget) return;

        try {
            const scope = resolveLibraryMutationScope([copyTarget]);
            const targetFolder = resolveLibraryMutationDestination(scope, copyTargetFolder);
            let data;
            if (scope.kind === 'case') {
                data = await simpleUploadService.copyFilesWithProgress(scope.keys, scope.caseId, targetFolder, { dispatch });
            } else {
                const response = await fetch('https://api.casedb.co/library.php', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        action: 'copy',
                        source_keys: scope.keys,
                        target_folder: targetFolder
                    })
                });
                data = await response.json();
            }

            if (data.success) {
                setShowCopyModal(false);
                setCopyTarget(null);
                setCopyTargetFolder('');
                refreshAfterLibraryMutation();
            } else {
                alert('Failed to copy file: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error copying file:', error);
            alert(error.message || 'Failed to copy file. Please try again.');
        }
    };

    // Open file in new tab (double-click or view button)
    const handleFileOpenInNewTab = async (file, options = {}) => {
        // Navigate to file viewer with the file ID
        if (file.id) {
            const params = new URLSearchParams();
            if (options.registryId) {
                params.set('registry_id', String(options.registryId));
            }
            if (options.chunkIndex !== undefined && options.chunkIndex !== null) {
                params.set('chunk_index', String(options.chunkIndex));
            }
            if (options.pdfHighlight) {
                params.set('pdf_highlight', '1');
            }
            if (options.chunkToken) {
                params.set('chunk_token', options.chunkToken);
            }
            const queryString = params.toString();
            window.open(`/file/${file.id}${queryString ? `?${queryString}` : ''}`, '_blank');
        } else {
            console.error('File ID not available for:', file);
        }
    };

    const handleOfficeEdit = async (file) => {
        if (!file?.id) {
            alert('This library document is still being indexed. Please refresh the library and try again.');
            return;
        }

        if (!isOfficeFile(file)) {
            alert('Only Word, Excel, and PowerPoint files can be edited in Office apps.');
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/edit-session.php/${file.id}/editing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || 'Failed to create Office edit session');
            }

            const editUrl = result.editUrl || result.webEditUrl || result.directUrl || result.fallbackUrl;
            if (!editUrl) {
                throw new Error('No Office edit URL was returned');
            }

            if (result.editUrl) {
                window.location.href = result.editUrl;
            } else {
                window.open(editUrl, '_blank', 'noopener');
            }
        } catch (error) {
            console.error('Error opening Office editor:', error);
            alert(`Unable to open Office editor: ${error.message}`);
        }
    };

    // Folder rename handler
    const handleRenameFolder = (folder) => {
        setRenameTarget({ ...folder, isFolder: true });
        setNewFileName(folder.name);
        setShowRenameModal(true);
    };

    // Folder delete handler
    const handleDeleteFolder = async (folder) => {
        if (!window.confirm(`Are you sure you want to delete the folder "${folder.name}" and all its contents?`)) {
            return;
        }

        try {
            const scope = resolveLibraryMutationScope([folder]);
            let data;
            if (scope.kind === 'case') {
                data = await simpleUploadService.deleteFiles(scope.keys, scope.caseId);
            } else {
                const response = await fetch('https://api.casedb.co/library.php', {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        keys: scope.keys,
                        is_folder: true
                    })
                });
                data = await response.json();
            }

            if (data.success) {
                refreshAfterLibraryMutation();
            } else {
                alert('Failed to delete folder: ' + (data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
            alert(error.message || 'Failed to delete folder. Please try again.');
        }
    };

    // Full file display functionality (double-click)
    const handleFileDoubleClick = async (file) => {
        if (file.type === 'folder') {
            handleFolderClick(file);
            return;
        }

        // Navigate to file viewer with the file ID
        if (file.id) {
            navigate(`/file/${file.id}`);
        } else {
            console.error('File ID not available for:', file);
        }
    };

    // Selection handling
    const toggleFileSelection = (fileKey) => {
        setSelectedFiles(prev => 
            prev.includes(fileKey) 
                ? prev.filter(key => key !== fileKey)
                : [...prev, fileKey]
        );
    };

    const toggleFolderSelection = (folderName) => {
        setSelectedFolders(prev => 
            prev.includes(folderName) 
                ? prev.filter(name => name !== folderName)
                : [...prev, folderName]
        );
    };

    // Create folder handler
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            alert('Please enter a folder name');
            return;
        }

        try {
            const targetPath = view === 0 
                ? (folderPath || 'library')
                : (selectedCase ? `cases/${selectedCase.s3_case_id || selectedCase.id}/{{Name}}${folderPath ? '/' + folderPath : ''}` : '');

            const endpoint = view === 0 ? 'library.php' : 's3_upload.php';
            
            const response = await fetch(`https://api.casedb.co/${endpoint}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create_folder',
                    folder_path: targetPath,
                    folder_name: newFolderName.trim(),
                    ...(view === 1 && selectedCase ? { case_id: selectedCase.id } : {})
                })
            });

            const data = await response.json();

            if (data.success) {
                setShowCreateFolderModal(false);
                setNewFolderName('');
                
                // Reload current view
                if (view === 0) {
                    loadLibraryFiles(folderPath);
                } else if (selectedCase) {
                    loadCaseDocuments(selectedCase.id, folderPath);
                }
            } else {
                alert(`Failed to create folder: ${data.message}`);
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            alert('Error creating folder. Please try again.');
        }
    };

    // Upload files handler
    const handleUploadFiles = async (files) => {
        if (!files || files.length === 0) return;

        if (view === 1 && !selectedCase) {
            alert('Select a case to upload files.');
            return;
        }

        const endpoint = view === 0 ? 'library.php' : 's3_upload.php';
        
        // Default to user_id 12 if not set
        const uploadUserId = user_id || 12;
        
        // Track completed uploads
        let completedCount = 0;
        const totalFiles = files.length;

        // Add each file to the upload context
        Array.from(files).forEach((file, index) => {
            const uploadId = `${Date.now()}-${index}`;
            
            // Add to upload context
            dispatch({
                type: 'START_UPLOAD',
                payload: {
                    id: uploadId,
                    fileName: file.name,
                    fileSize: file.size,
                    operation: 'upload'
                }
            });

            // Create FormData for this file
            const formData = new FormData();
            
            if (view === 0) {
                // Library upload
                formData.append('files[]', file);
                formData.append('target_path', folderPath || 'library');
                formData.append('user_id', uploadUserId);
            } else {
                // Case upload - s3_upload.php expects different params
                formData.append('file', file); // Single file, not array
                formData.append('folder_path', folderPath || '{{Name}}'); // folder_path not target_path
                formData.append('case_id', selectedCase.id);
                formData.append('user_id', uploadUserId);
            }

            // Upload the file
            fetch(`https://api.casedb.co/${endpoint}`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    dispatch({
                        type: 'COMPLETE_UPLOAD',
                        payload: {
                            id: uploadId
                        }
                    });
                } else {
                    dispatch({
                        type: 'FAIL_UPLOAD',
                        payload: {
                            id: uploadId,
                            error: data.message || 'Upload failed'
                        }
                    });
                }
            })
            .catch(error => {
                console.error('Error uploading file:', error);
                dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: {
                        id: uploadId,
                        error: error.message || 'Upload failed'
                    }
                });
            })
            .finally(() => {
                // Increment completed count
                completedCount++;
                
                // Reload current view after all files complete (success or failure)
                if (completedCount === totalFiles) {
                    setTimeout(() => {
                        if (view === 0) {
                            loadLibraryFiles(folderPath, pagination.currentPage);
                        } else if (selectedCase) {
                            loadCaseDocuments(selectedCase.id, folderPath, pagination.currentPage);
                        }
                    }, 500);
                }
            });
        });
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragCounter(prev => prev + 1);

        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
            if (hasFiles) {
                setIsDragOver(true);
            }
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragCounter(prev => {
            const newCount = prev - 1;
            if (newCount <= 0) {
                setIsDragOver(false);
                return 0;
            }
            return newCount;
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragOver(false);
        setDragCounter(0);

        if (view === 1 && !selectedCase) {
            alert('Select a case to upload files.');
            return;
        }

        const droppedFiles = Array.from(e.dataTransfer.files || []);
        if (droppedFiles.length > 0) {
            await handleUploadFiles(droppedFiles);
        }
    };

    // Handle file input change
    const handleFileInputChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleUploadFiles(files);
        }
        // Reset input so same file can be uploaded again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Trigger file input click
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle URL parameters on mount and browser navigation
    useEffect(() => {
        const urlParams = parseURLParams();
        if (urlParams.view !== null && urlParams.view !== view) {
            setView(urlParams.view);
        }
        
        // Only update state if it differs from URL
        const targetView = urlParams.view ?? view;

        if (urlParams.folder !== folderPath || urlParams.search !== searchQuery) {
            setFolderPath(urlParams.folder);
            setSearchQuery(urlParams.search);

            if (urlParams.case) {
                loadCaseDocuments(urlParams.case, urlParams.folder);
                fetchCaseForURL(urlParams.case);
            } else if (targetView === 0) {
                loadLibraryFiles(urlParams.folder);
            } else if (targetView === 1) {
                loadCases();
            } else if (targetView === 2) {
                loadFirmWideFiles();
            }
        }
    }, [location.search]);

    // Helper function to fetch case info for URL-based navigation
    const fetchCaseForURL = async (caseId) => {
        try {
            const params = new URLSearchParams({
                mode: 'cases',
                case_id: caseId,
                folder: '{{Name}}',
                page: '1',
                page_size: '1'
            });

            const response = await fetch(`https://api.casedb.co/library.php?${params}`, {
                credentials: 'include'
            });

            const data = await response.json();
            if (data.success && data.case_info) {
                setSelectedCase({
                    id: data.case_info.id,
                    name: data.case_info.case_name,
                    client_name: data.case_info.client_name,
                    contact_full_name: data.case_info.contact_full_name,
                    file_count: data.case_info.file_count
                });
                setCaseInfo(data.case_info);
            }
        } catch (error) {
            console.error('Error fetching case for URL:', error);
        }
    };

    // Load initial data (fallback if no URL params)
    useEffect(() => {
        if (!location.search) {
            // No URL params, load default view
            if (view === 0) {
                loadLibraryFiles();
            } else if (view === 1) {
                loadCases();
            } else if (view === 2) {
                loadFirmWideFiles();
            }
        }
    }, []);

    // Debounced search
    useEffect(() => {
        if (!hasInitializedSearchEffectRef.current) {
            hasInitializedSearchEffectRef.current = true;
            return;
        }

        // Cancel any pending requests
        if (searchAbortControllerRef.current) {
            searchAbortControllerRef.current.abort();
        }

        // Create new abort controller for this search
        const abortController = new AbortController();
        searchAbortControllerRef.current = abortController;

        const hasSearch = searchQuery && searchQuery.length >= 2;
        if (hasSearch) {
            setIsSearchPending(true);
        } else {
            setIsSearchPending(false);
        }

        const timer = setTimeout(() => {
            if (searchQuery && searchQuery.length >= 2) {
                setIsSearchPending(false);
                if (view === 1 && !selectedCase) {
                    // Global search in cases view
                    performGlobalSearch(1, abortController.signal);
                } else if (view === 0) {
                    // Library file search
                    loadLibraryFiles(folderPath, 1, abortController.signal);
                } else if (view === 2) {
                    // Firm-wide file search
                    loadFirmWideFiles(1, abortController.signal);
                } else if (selectedCase) {
                    // Case document search
                    loadCaseDocuments(selectedCase.id, folderPath, 1, abortController.signal);
                }
            } else {
                setIsSearchPending(false);
                // No search query - load normal content
                if (view === 0) {
                    loadLibraryFiles(folderPath, 1, abortController.signal);
                } else if (view === 1 && !selectedCase) {
                    loadCases(1, abortController.signal);
                } else if (view === 2) {
                    loadFirmWideFiles(1, abortController.signal);
                } else if (selectedCase) {
                    loadCaseDocuments(selectedCase.id, folderPath, 1, abortController.signal);
                }
            }
        }, 300);

        setFiles([]); // Clear current files while searching/loading
        setFolders([]); // Clear current folders while searching/loading

        return () => {
            clearTimeout(timer);
            // Abort the request when component unmounts or search changes
            abortController.abort();
        };
    }, [searchQuery, view, selectedCase, useSemanticSearch]);

    // Handle extension filter changes
    useEffect(() => {
        if (!hasInitializedFilterEffectRef.current) {
            hasInitializedFilterEffectRef.current = true;
            return;
        }

        if (view === 0) {
            loadLibraryFiles(folderPath, 1);
        } else if (view === 2) {
            loadFirmWideFiles(1);
        } else if (selectedCase) {
            loadCaseDocuments(selectedCase.id, folderPath, 1);
        }
    }, [extensionFilter, firstPrimaryFilter]);

    useEffect(() => {
        const loadUsers = async () => {
            if (view !== 2) return;
            const users = await fetchUsers();
            if (Array.isArray(users)) {
                const sortedUsers = [...users].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
                );
                setFirstPrimaryUsers(sortedUsers);
            }
        };
        loadUsers();
    }, [view]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Render breadcrumbs with case context
    const renderBreadcrumbs = () => {
        const pathParts = folderPath.split('/').filter(Boolean);
        
        return (
            <div className="breadcrumbs">
                {/* Show back to cases button when in case mode */}
                {selectedCase && (
                    <>
                        <button 
                            className="breadcrumb-item back-button"
                            onClick={handleBackToCases}
                            title="Back to Cases"
                        >
                            ← Cases
                        </button>
                        <ChevronRight size={14} />
                    </>
                )}
                
                <button 
                    className="breadcrumb-item"
                    onClick={() => {
                        setFolderPath('');
                        updateURL({ 
                            folder: null,
                            ...(selectedCase && { case: selectedCase.id })
                        });
                        
                        if (view === 0) {
                            loadLibraryFiles();
                        } else if (selectedCase) {
                            loadCaseDocuments(selectedCase.id);
                        }
                    }}
                >
                    <Home size={16} />
                    {view === 0 ? 'Library' : (selectedCase?.name || 'Case')}
                </button>
                
                {pathParts.map((part, index) => (
                    <React.Fragment key={index}>
                        <ChevronRight size={14} />
                        <button
                            className="breadcrumb-item"
                            onClick={() => handleBreadcrumbClick(index)}
                        >
                            {index === 2 && pathParts[0]?.toLowerCase() === 'docgen' && /^\d+$/.test(part) ? (docGenFolderLabels[part] || part) : part}
                        </button>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Render file/folder/case grid
    const renderGrid = () => {
        let allItems = [];
        
        if (view === 1 && !selectedCase) {
            if (searchQuery && searchQuery.length >= 2) {
                // Global search: Show both cases and files mixed together
                allItems = [...cases.map(c => ({ ...c, type: 'case' })), ...files.map(f => ({ ...f, type: 'file' }))];
            } else {
                // Normal cases view: Show cases only
                allItems = cases.map(c => ({ ...c, type: 'case' }));
            }
        } else if (view === 2) {
            // Firm-wide view: Show all files flattened (no folders, no cases)
            allItems = files.map(f => ({ ...f, type: 'file' }));
        } else {
            // View 0 or case documents: Show folders and files
            allItems = [...folders.map(f => ({ ...f, type: 'folder' })), ...files.map(f => ({ ...f, type: 'file' }))];
        }
        
        // Empty state handling
        if (allItems.length === 0) {
            if (isSearchPending && searchQuery && searchQuery.length >= 2) {
                return (
                    <div className="empty-state" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        gap: '20px',
                        textAlign: 'center'
                    }}>
                        <Search size={48} style={{ color: 'var(--text-muted)' }} />
                        <div>
                            <h3>Search in progress</h3>
                            <p className="subtext">Searching for “{searchQuery}”...</p>
                        </div>
                    </div>
                );
            }
            // Show "Search firm wide?" prompt if searching in view 0 or 1 with a query
            if (searchQuery && searchQuery.length >= 2 && (view === 0 || view === 1) && view !== 2) {
                return (
                    <div className="empty-state" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        gap: '20px'
                    }}>
                        <Search size={48} style={{ color: 'var(--text-muted)' }} />
                        <div style={{ textAlign: 'center' }}>
                            <h3>No results found in {view === 0 ? 'library' : 'cases'}</h3>
                            <p className="subtext">Your search "{searchQuery}" didn't match any {view === 0 ? 'library files' : 'cases or files'}</p>
                        </div>
                        <button
                            className="action primary"
                            onClick={() => {
                                handleViewChange(2);
                            }}
                            style={{
                                marginTop: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <Search size={16} />
                            Search firm wide?
                        </button>
                    </div>
                );
            }
            
            // Regular empty state
            return (
                <div className="empty-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    gap: '20px',
                    textAlign: 'center'
                }}>
                    {view === 0 && <BookCopy size={48} style={{ color: 'var(--text-muted)' }} />}
                    {view === 1 && <Archive size={48} style={{ color: 'var(--text-muted)' }} />}
                    {view === 2 && <FileText size={48} style={{ color: 'var(--text-muted)' }} />}
                    <div>
                        <h3>
                            {view === 0 && 'No library files'}
                            {view === 1 && !selectedCase && 'No cases found'}
                            {view === 1 && selectedCase && 'No documents in this case'}
                            {view === 2 && 'No documents found'}
                        </h3>
                        <p className="small-text" style={{ color: 'var(--subtext-color)' }}>
                            {view === 0 && 'Upload files to get started'}
                            {view === 1 && !selectedCase && 'No cases match your search'}
                            {view === 1 && selectedCase && 'Upload documents to this case'}
                            {view === 2 && 'No documents found across the firm'}
                        </p>
                    </div>
                </div>
            );
        }


        // ONLY if viewing case documents (selectedCase exists), use table layout like DocumentSection.js
        if (view === 1 && selectedCase) {
            return (
                <table className="exhibits">
                    <thead style={{ width: '100%' }}>
                        <tr>
                            <th className="file-name">
                                <span>Name</span>
                            </th>
                            <th className="file-date">
                                <span>Last Modified</span>
                            </th>
                            <th className="file-size">
                                <span>Size</span>
                            </th>
                            <th className="file-author">
                                <span>Author</span>
                            </th>
                            <th className="file-actions">...</th>
                        </tr>
                    </thead>
                    <tbody style={{ width: '100%' }}>
                        {allItems.map((item, index) => {
                            if (item.type === 'folder') {
                                return (
                                    <tr
                                        key={`folder-${index}`}
                                        onClick={() => handleFolderClick(item)}
                                        onDoubleClick={() => handleFolderClick(item)}
                                        className={`exhibit ${selectedFolders.includes(getFolderNavigationName(item)) ? 'active-file' : ''}`}
                                    >
                                        <td className="file-name folder subtext">
                                            {/* <div className="file-select">
                                                Checkbox can be added here if needed
                                            </div> */}
                                            <Folder size={16} />
                                            <div className='file-preview-trigger'>
                                                {getFolderDisplayName(item)}
                                            </div>
                                        </td>
                                        <td className="file-date folder subtext">
                                            {formatDate(item.lastModified || item.last_modified)}
                                        </td>
                                        <td className="file-size folder subtext">-</td>
                                        <td className="file-author folder subtext">-</td>
                                        <td className="file-actions subtext" onClick={(e) => e.stopPropagation()}>
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    className="file-actions-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDropdownClick(e, `folder-${item.name}`);
                                                    }}
                                                >
                                                    ⋯
                                                </button>
                                                {activeDropdown === `folder-${item.name}` && (
                                                    <div
                                                        ref={dropdownRef}
                                                        className="file-actions-dropdown"
                                                        style={{
                                                            position: 'absolute',
                                                            right: 0,
                                                            ...(dropdownPosition === 'top' 
                                                                ? { bottom: '100%', top: 'auto' } 
                                                                : { top: '100%', bottom: 'auto' }),
                                                            zIndex: 1000
                                                        }}
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRenameFolder(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                        >
                                                            <Edit size={14} />
                                                            Rename
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteFolder(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                color: 'var(--priority-color)'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            } else {
                                // File row
                                return (
                                    <tr
                                        key={`file-${index}`}
                                        onClick={() => handleFilePreview(item)}
                                        onDoubleClick={() => {
                                            handleFileOpenInNewTab(item);
                                        }}
                                        className={`exhibit ${selectedFiles.includes(item.key) ? 'active-file' : ''}`}
                                    >
                                        <td className="file-name subtext">
                                            {/*<div className="file-select">
                                                {/* Checkbox can be added here if needed}
                                            </div>*/}
                                            <FileText size={16} />
                                            <div
                                                title={item.name}
                                                className="subtext file-preview-trigger"
                                            >
                                                {item.name}
                                            </div>
                                        </td>
                                        <td className="file-date subtext">
                                            {formatDate(item.lastModified || item.last_modified)}
                                        </td>
                                        <td className="file-size subtext">
                                            {formatFileSize(item.size)}
                                        </td>
                                        <td className="file-author subtext">
                                            {item.author || item.uploaded_by || '-'}
                                        </td>
                                        <td className='file-actions subtext'>
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    className="file-actions-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDropdownClick(e, item.key);
                                                    }}
                                                >
                                                    ⋯
                                                </button>
                                                {activeDropdown === item.key && (
                                                    <div
                                                        ref={dropdownRef}
                                                        className="file-actions-dropdown"
                                                        style={{
                                                            position: 'absolute',
                                                            right: 0,
                                                            ...(dropdownPosition === 'top' 
                                                                ? { bottom: '100%', top: 'auto' } 
                                                                : { top: '100%', bottom: 'auto' }),
                                                            zIndex: 1000
                                                        }}
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleFileOpenInNewTab(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Eye size={14} />
                                                            View File
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleFileDownload(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Download size={14} />
                                                            Download
                                                        </button>
                                                        {isOfficeFile(item) && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOfficeEdit(item);
                                                                    setActiveDropdown(null);
                                                                }}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px 15px',
                                                                    border: 'none',
                                                                    background: 'none',
                                                                    textAlign: 'left',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    fontSize: '14px'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                            >
                                                                <Edit size={14} />
                                                                Edit in Office
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRenameFile(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Edit size={14} />
                                                            Rename
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMoveFile(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Move size={14} />
                                                            Move
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCopyFile(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Copy size={14} />
                                                            Copy
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleFileDelete(item);
                                                                setActiveDropdown(null);
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '10px 15px',
                                                                border: 'none',
                                                                background: 'none',
                                                                textAlign: 'left',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '14px',
                                                                color: 'var(--danger-color)'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-color)'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                        })}
                    </tbody>
                </table>
            );
        }
        
        // Default grid view for everything else (library, cases list, library folders)
        return !loading && (
            <div className="file-grid">
                {allItems.map((item, index) => (
                    <div
                        key={item.type === 'folder' ? `folder-${item.path || item.name}` : 
                             item.type === 'case' ? `case-${item.id}` : 
                             `file-${item.key}`}
                        className={`library-card ${item.type === 'file' && selectedFiles.includes(item.key) ? ' selected' : ''} ${item.type === 'folder' && selectedFolders.includes(getFolderNavigationName(item)) ? 'selected' : ''}`}
                        onClick={() => {
                            if (item.type === 'folder') {
                                handleFolderClick(item);
                            } else if (item.type === 'case') {
                                handleCaseClick(item);
                            } else {
                                handleFilePreview(item);
                            }
                        }}
                        onDoubleClick={() => {
                            if (item.type === 'file') {
                                handleFileOpenInNewTab(item);
                            }
                        }}
                    >
                        <div className="library-card-icon">
                            {item.type === 'folder' ? (
                                <Folder size={40} />
                            ) : item.type === 'case' ? (
                                <span style={{ ...getContactInitialsClassName(item?.id, {color: true}), color: 'black', textAlign: 'center', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{item?.contact_full_name}</span>
                            ) : (
                                <div
                                    className="library-file-extension"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <File size={28} />
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            color: 'var(--text-color)'
                                        }}
                                    >
                                        {(() => {
                                            const rawName = item?.name || '';
                                            const parts = rawName.split('.');
                                            if (parts.length < 2) return 'FILE';
                                            const ext = parts.pop();
                                            return (ext || 'FILE').toString().toUpperCase();
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className={`library-card-info ${item.type === 'folder' ? 'folder-library-card' : 'file-library-card'}`} title={item.type === 'folder' ? getFolderDisplayName(item) : item.name}>
                            {item.type === 'folder' &&
                                <>
                                    <span className='library-card-name'>{getFolderDisplayName(item)}</span>
                                    <div className='library-card-meta'>
                                        <span className="library-file-size subtext">{item.children_count} items</span>
                                        <span className='library-file-date subtext'>{formatDate(item.created)}</span>
                                    </div>
                                </>
                            }

                            {item.type === 'file' &&
                                <>
                                    <span className='library-card-name'>{item.name}</span>
                                    <div className='library-card-details'>
                                        {item?.case_name && <span className="library-file-case-name">{item.case_name}</span>}
                                        {item?.case_type_name && <span className="library-file-first">{item.case_type_name}</span>}
                                        {item?.first_primary_name && <span className="library-file-first">{item.first_primary_name}</span>}
                                        {(item?.matched_chunks?.[0]?.grouped_text || item?.matched_chunks?.[0]?.snippet) && (
                                            <span className="library-intelligence-snippet">
                                                {(item.matched_chunks[0].grouped_text || item.matched_chunks[0].snippet).replace(/<[^>]*>/g, '')}
                                            </span>
                                        )}
                                        <div className={`library-card-meta`}>
                                            {item?.size > 0 && <span className="library-file-size subtext">{formatFileSize(item.size)}</span>}
                                            <span className='library-file-date subtext'>{formatDate(item.lastModified || item.last_modified)}</span>
                                        </div>
                                    </div>
                                </>
                            }
                            
                            {item.type === 'case' && (
                                <div className="library-case-info">
                                    <span className='library-card-case-name'>{item.name}</span>
                                    {/*(item.contact_full_name || item.client_name) && (
                                        <span className="library-case-client">
                                            Client: {item.contact_full_name || item.client_name}
                                        </span>
                                    )*/}
                                    <div className='library-card-meta'>
                                        <span className="library-case-files">{item.file_count} files</span>
                                        <span className="library-case-date">{formatDate(item.lastModified || item.last_modified)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {item.type === 'folder' && view !== 0 && (
                            <div className='library-actions'>
                                <div className="library-action-menu" style={{ position: 'relative' }}>
                                    <div
                                        className="library-action"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDropdownClick(e, `grid-folder-${item.name}`);
                                        }}
                                        title="Actions"
                                    >
                                        <Ellipsis size={14} />
                                    </div>
                                    {activeDropdown === `grid-folder-${item.name}` && (
                                        <div
                                            ref={dropdownRef}
                                            className="library-actions-dropdown"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                ...(dropdownPosition === 'top' 
                                                    ? { bottom: '100%', top: 'auto' } 
                                                    : { top: '100%', bottom: 'auto' }),
                                                zIndex: 1000
                                            }}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRenameFolder(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Edit size={14} />
                                                Rename
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteFolder(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {item.type === 'file' && (
                            <div className='library-actions'>
                                <div className='library-first-actions'>
                                    <div className='library-action'>
                                        <ArrowDownToLine 
                                            size={14} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFileDownload(item);
                                            }}
                                            title="Download File"
                                        />
                                    </div>
                                    <div className='library-action'>
                                        <Eye 
                                            size={14} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFileOpenInNewTab(item);
                                            }} 
                                            title="Open in New Tab" 
                                        />
                                    </div>
                                </div>
                                <div className="library-action-menu" style={{ position: 'relative' }}>
                                    <div
                                        className="library-action"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDropdownClick(e, `grid-${item.key}`);
                                        }}
                                        title="Actions"
                                    >
                                        <Ellipsis size={14} />
                                    </div>
                                    {activeDropdown === `grid-${item.key}` && (
                                        <div
                                            ref={dropdownRef}
                                            className="library-actions-dropdown"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                ...(dropdownPosition === 'top' 
                                                    ? { bottom: '100%', top: 'auto' } 
                                                    : { top: '100%', bottom: 'auto' }),
                                                zIndex: 1000
                                            }}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileOpenInNewTab(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Eye size={14} />
                                                View File
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileDownload(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Download size={14} />
                                                Download
                                            </button>
                                            {isOfficeFile(item) && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOfficeEdit(item);
                                                        setActiveDropdown(null);
                                                    }}
                                                >
                                                    <Edit size={14} />
                                                    Edit in Office
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRenameFile(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Edit size={14} />
                                                Rename
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveFile(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Move size={14} />
                                                Move
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopyFile(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Copy size={14} />
                                                Copy
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileDelete(item);
                                                    setActiveDropdown(null);
                                                }}
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // Utility functions
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    const inputRef = useRef(null);
    
    // Handle search input focus
    const handleFocus = () => {
        // Focus immediately if modal
        if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <div className={`page-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id="page-header">
                <h2>{caseInfo?.case_name ? `${caseInfo.case_name} ` : 'Document Library'}</h2>
                <div className='this-or-that'>
                    <div
                        className={`tot-option ${ view === 0 ? 'active' : '' }`}
                        onClick={() => handleViewChange(0)}
                        title='Library documents'
                    >
                        <SquareLibrary />
                    </div>
                    <div
                        className={`tot-option${ view === 1 ? ' active' : '' }`}
                        onClick={() => handleViewChange(1)}
                        title='All cases'
                    >
                        <GalleryVerticalEnd />
                    </div>
                    <div
                        className={`tot-option${ view === 2 ? ' active' : '' }`}
                        onClick={() => handleViewChange(2)}
                        title='Firm-wide documents (all files flattened)'
                    >
                        <ArrowDownAZ />
                    </div>
                </div>
                <span className='subtext'>
                    {view === 0 && 'Library of general documents (non-case docs)'}
                    {view === 1 && 'Browse all cases and their documents'}
                    {view === 2 && 'Search all firm documents in one place (library + cases, flattened)'}
                </span>
            </div>
            <div className="library-controls">
                <div className="search-section">
                    <div className="search-input-container" style={{ width: '100%', display: "flex", justifyContent: 'flex-start', position: 'relative' }} onClick={handleFocus}>
                        <Search size={20} className="search-icon" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={
                                view === 0 ? "Search library files..." : 
                                view === 2 ? "Search all firm documents..." :
                                selectedCase ? "Search case documents..." : 
                                "Search cases and files..."
                            }
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                            onFocus={handleFocus}
                            className="library-search-input"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className="textarea-clear-button-absolute">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {view === 2 && (
                    <>
                        <Checkbox value={useSemanticSearch} onChange={() => setUseSemanticSearch(!useSemanticSearch)} label="Semantically match file names" hint='Uses filename embeddings to rank likely file-name matches instead of searching document chunks.' />
                        <div className="filter-group" style={{ flexShrink: 0, height: '36px' }}>
                            <div className='select-wrapper'>
                                <select
                                    value={firstPrimaryFilter}
                                    onChange={(e) => setFirstPrimaryFilter(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">All First Primaries</option>
                                    {firstPrimaryUsers.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    </>
                )}

                {selectedCase && (
                    <div className="library-case-navigation">
                        <button
                            className="action quaternary"
                            style={{ backgroundColor: 'var(--secondary-color)' }}
                            onClick={handleBackToCases}
                        >
                            <ChevronRight size={16} style={{transform: 'rotate(180deg)'}} />
                            Back
                        </button>
                    </div>
                )}

                <div className="filter-group" style={{ height: '36px' }}>
                    <div className='select-wrapper'>
                        <select
                            value={extensionFilter}
                            onChange={(e) => setExtensionFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Files</option>
                            <option value="pdf">PDF</option>
                            <option value="doc">DOC</option>
                            <option value="docx">DOCX</option>
                            <option value="xls">XLS</option>
                            <option value="xlsx">XLSX</option>
                            <option value="txt">TXT</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                        </select>
                        <ChevronDown size={16} className="select-chevron" />
                    </div>
                </div>

                {/* Action buttons - only show when in library view or viewing a case */}
                {(view === 0 || selectedCase) && (
                    <div className="library-action-buttons" style={{ display: 'flex', gap: '10px', width: '100%' }}>
                        <button
                            className={`action ${currentAction === 'upload' ? 'tertiary' : 'quaternary'}`}
                            onMouseEnter={() => setCurrentAction('upload')}
                            onMouseLeave={() => setCurrentAction(null)}
                            style={{ backgroundColor: 'var(--secondary-color)' }}
                            onClick={handleUploadClick}
                            title="Upload files"
                        >
                            <Upload size={16} />
                            Upload
                        </button>
                        <button
                            className={`action ${currentAction === 'newFolder' ? 'tertiary' : 'quaternary'}`}
                            onMouseEnter={() => setCurrentAction('newFolder')}
                            onMouseLeave={() => setCurrentAction(null)}
                            style={{ backgroundColor: 'var(--secondary-color)' }}
                            onClick={() => setShowCreateFolderModal(true)}
                            title="Create new folder"
                        >
                            <Folder size={16} />
                            New Folder
                        </button>
                    </div>
                )}
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />

            {/* Breadcrumbs - only show when navigating folders, not when viewing cases list */}
            {(view === 0 || selectedCase) && folderPath && renderBreadcrumbs()}

            {/* Content Area */}
            <div
                className="library-content"
                style={{ display: 'flex', gap: '20px', position: 'relative' }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {isDragOver && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(59, 130, 246, 0.08)',
                            border: '2px dashed rgba(59, 130, 246, 0.6)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            pointerEvents: 'none'
                        }}
                    >
                        <span className="subtext">Drop files to upload</span>
                    </div>
                )}
                <div className="main-content" style={{ flex: sidePreview ? '1' : '1 1 100%' }}>
                    {loading ? (
                        <div className="loading-state">
                            <Loader2 className="spinner" />
                            <p>Loading {
                                view === 1 && !selectedCase ? 'cases' : 
                                view === 2 ? 'firm-wide documents' :
                                'documents'
            }...</p>
                        </div>
                    ) : (
                        <React.Fragment key={searchQuery}>
                            {renderGrid()}                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="library-pagination">
                                    <button
                                        className='action alt'
                                        disabled={pagination.currentPage === 1}
                                        onClick={() => {
                                            const newPage = pagination.currentPage - 1;
                                            if (searchQuery && searchQuery.length >= 2 && view === 1 && !selectedCase) {
                                                performGlobalSearch(newPage);
                                            } else if (view === 0) {
                                                loadLibraryFiles(folderPath, newPage);
                                            } else if (view === 1 && !selectedCase) {
                                                loadCases(newPage);
                                            } else if (view === 2) {
                                                loadFirmWideFiles(newPage);
                                            } else if (selectedCase) {
                                                loadCaseDocuments(selectedCase.id, folderPath, newPage);
                                            }
                                        }}
                                    >
                                        Previous
                                    </button>
                                    
                                    <span className='subtext'>
                                        Page {pagination.currentPage} of {pagination.totalPages}
                                        &nbsp;({formatValue(pagination.totalCount)} total {view === 1 && !selectedCase ? 'results' : 'items'})
                                    </span>
                                    
                                    <button
                                        className='action alt'
                                        disabled={pagination.currentPage === pagination.totalPages}
                                        onClick={() => {
                                            const newPage = pagination.currentPage + 1;
                                            if (searchQuery && searchQuery.length >= 2 && view === 1 && !selectedCase) {
                                                performGlobalSearch(newPage);
                                            } else if (view === 0) {
                                                loadLibraryFiles(folderPath, newPage);
                                            } else if (view === 1 && !selectedCase) {
                                                loadCases(newPage);
                                            } else if (view === 2) {
                                                loadFirmWideFiles(newPage);
                                            } else if (selectedCase) {
                                                loadCaseDocuments(selectedCase.id, folderPath, newPage);
                                            }
                                        }}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </div>
                
                {/* Side Preview Panel */}
                {sidePreview && (
                    <div className="file-preview-panel" style={{
                        position: 'sticky',
                        top: '12px',
                        alignSelf: 'flex-start',
                        maxHeight: 'calc(100vh - 140px)',
                        overflow: 'hidden'
                    }}>
                        <div className="preview-panel-header">
                            <h5>{sidePreviewFile?.name || 'Preview'}</h5>
                            <button
                                className="close-preview-panel"
                                onClick={() => {
                                    setSidePreview(null);
                                    setSidePreviewFile(null);
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="preview-panel-content" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                            {sidePreview}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showFileEditor && fileToEdit && (
                <FileEditor
                    file={fileToEdit}
                    isOpen={showFileEditor}
                    onClose={() => {
                        setShowFileEditor(false);
                        setFileToEdit(null);
                    }}
                    onSave={(file, content) => {
                        console.log('File saved:', file.name);
                        // Refresh current view
                        if (view === 'library') {
                            loadLibraryFiles(folderPath, pagination.currentPage);
                        } else if (selectedCase) {
                            loadCaseDocuments(selectedCase.id, pagination.currentPage, view === 'flattened');
                        }
                    }}
                    case_id={selectedCase?.id || 'library'}
                    user_id={user_id}
                />
            )}

            {/* Create Folder Modal */}
            {showCreateFolderModal && (
                <Modal
                    isOpen={showCreateFolderModal}
                    onClose={() => {
                        setShowCreateFolderModal(false);
                        setNewFolderName('');
                    }}
                    title="Create New Folder"
                >
                    <div className="modal-content-wrapper">
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            Folder Name:
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCreateFolder();
                                    }
                                }}
                                placeholder="Enter folder name"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    marginTop: '5px',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px'
                                }}
                                autoFocus
                            />
                        </label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowCreateFolderModal(false);
                                    setNewFolderName('');
                                }}
                                className="alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim()}
                            >
                                Create Folder
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Rename File/Folder Modal */}
            {showRenameModal && renameTarget && (
                <Modal
                    isOpen={showRenameModal}
                    title="Rename"
                    instructions="Enter a new name for the item."
                    onClose={() => {
                        setShowRenameModal(false);
                        setRenameTarget(null);
                        setNewFileName('');
                    }}
                    header={
                        <div className='modal-header-actions'>
                            <button
                                onClick={handleRenameSubmit}
                                disabled={!newFileName.trim() || renameSubmitting}
                                className="action"
                            >
                                {renameSubmitting ? 'Starting...' : 'Rename'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowRenameModal(false);
                                    setRenameTarget(null);
                                    setNewFileName('');
                                }}
                                className="action alt"
                            >
                                Cancel
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='rename-files'>
                            <div className='form-group full'>
                                <label className="subtext">{renameTarget.name}</label>
                                <div className='file-rename-container'>
                                    <input
                                        type="text"
                                        value={newFileName}
                                        onChange={(e) => setNewFileName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !renameSubmitting) {
                                                handleRenameSubmit();
                                            }
                                        }}
                                        placeholder={renameTarget.isFolder ? "New folder name" : "New name"}
                                        autoFocus
                                    />
                                    {!renameTarget.isFolder && (() => {
                                        const originalName = renameTarget.name || '';
                                        if (!originalName.includes('.')) return null;
                                        const extension = originalName.split('.').pop();
                                        return <span className="subtext" style={{ paddingBottom: "5px" }}>.{extension}</span>;
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Move File Modal */}
            {showMoveModal && actionTarget && (
                <Modal
                    isOpen={showMoveModal}
                    onClose={() => {
                        setShowMoveModal(false);
                        setActionTarget(null);
                        setMoveTargetFolder('');
                    }}
                    title="Move File"
                >
                    <div className="modal-content-wrapper">
                        <p style={{ marginBottom: '15px', color: 'var(--text-secondary)' }}>
                            Moving: <strong>{actionTarget.name}</strong>
                        </p>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            Target Folder Path:
                            <input
                                type="text"
                                value={moveTargetFolder}
                                onChange={(e) => setMoveTargetFolder(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleMoveSubmit();
                                    }
                                }}
                                placeholder={actionTarget?.key?.startsWith('cases/') ? '{{Name}}/subfolder' : 'library/subfolder'}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    marginTop: '5px',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px'
                                }}
                                autoFocus
                            />
                        </label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowMoveModal(false);
                                    setActionTarget(null);
                                    setMoveTargetFolder('');
                                }}
                                className="alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMoveSubmit}
                                disabled={!moveTargetFolder.trim()}
                            >
                                Move
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Copy File Modal */}
            {showCopyModal && copyTarget && (
                <Modal
                    isOpen={showCopyModal}
                    onClose={() => {
                        setShowCopyModal(false);
                        setCopyTarget(null);
                        setCopyTargetFolder('');
                    }}
                    title="Copy File"
                >
                    <div className="modal-content-wrapper">
                        <p style={{ marginBottom: '15px', color: 'var(--text-secondary)' }}>
                            Copying: <strong>{copyTarget.name}</strong>
                        </p>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            Target Folder Path:
                            <input
                                type="text"
                                value={copyTargetFolder}
                                onChange={(e) => setCopyTargetFolder(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCopySubmit();
                                    }
                                }}
                                placeholder={copyTarget?.key?.startsWith('cases/') ? '{{Name}}/subfolder' : 'library/subfolder'}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    marginTop: '5px',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px'
                                }}
                                autoFocus
                            />
                        </label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowCopyModal(false);
                                    setCopyTarget(null);
                                    setCopyTargetFolder('');
                                }}
                                className="alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCopySubmit}
                                disabled={!copyTargetFolder.trim()}
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default Library;
